import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { mergeMap, map, catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { WorkOrderActions } from './work-orders.actions';
import { WorkOrderService } from '../../services/work-order.service';
import { CylindersActions } from '../cylinders/cylinders.actions';

@Injectable()
export class WorkOrdersEffects {
  private actions$ = inject(Actions);
  private svc = inject(WorkOrderService);

  loadWorkOrders$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WorkOrderActions.loadWorkOrders),
      mergeMap(({ date, technicianId, from, to, status }) => this.svc.getAll(date, technicianId, from, to, status).pipe(
        map(workOrders => WorkOrderActions.loadWorkOrdersSuccess({ workOrders })),
        catchError(err => of(WorkOrderActions.loadWorkOrdersFailure({ error: err.message })))
      ))
    )
  );

  loadWorkOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WorkOrderActions.loadWorkOrder),
      mergeMap(({ id }) => this.svc.getById(id).pipe(
        map(workOrder => WorkOrderActions.loadWorkOrderSuccess({ workOrder })),
        catchError(err => of(WorkOrderActions.workOrderFailure({ error: err.message })))
      ))
    )
  );

  createWorkOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WorkOrderActions.createWorkOrder),
      mergeMap(({ dto }) => this.svc.create(dto).pipe(
        map(workOrder => WorkOrderActions.createWorkOrderSuccess({ workOrder })),
        catchError(err => of(WorkOrderActions.workOrderFailure({ error: err.message })))
      ))
    )
  );

  attachCylinder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WorkOrderActions.attachCylinder),
      mergeMap(({ workOrderId, cylinderId }) => this.svc.attachCylinder(workOrderId, cylinderId).pipe(
        mergeMap(workOrder => [
          WorkOrderActions.attachCylinderSuccess({ workOrder }),
          CylindersActions.loadCylinders({})
        ]),
        catchError(err => of(WorkOrderActions.workOrderFailure({ error: err.message })))
      ))
    )
  );

  issueWorkOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WorkOrderActions.issueWorkOrder),
      mergeMap(({ id }) => this.svc.issue(id).pipe(
        map(workOrder => WorkOrderActions.issueWorkOrderSuccess({ workOrder })),
        catchError(err => of(WorkOrderActions.workOrderFailure({ error: err.message })))
      ))
    )
  );

  completeWorkOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WorkOrderActions.completeWorkOrder),
      mergeMap(({ id }) => this.svc.complete(id).pipe(
        map(workOrder => WorkOrderActions.completeWorkOrderSuccess({ workOrder })),
        catchError(err => of(WorkOrderActions.workOrderFailure({ error: err.message })))
      ))
    )
  );

  // Part B/Phase 8 — spare-part custody.
  loadSparePartCustody$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WorkOrderActions.loadSparePartCustody),
      mergeMap(({ workOrderId }) => this.svc.getSparePartCustody(workOrderId).pipe(
        map(custody => WorkOrderActions.loadSparePartCustodySuccess({ workOrderId, custody })),
        catchError(err => of(WorkOrderActions.workOrderFailure({ error: err.message })))
      ))
    )
  );

  issueSparePart$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WorkOrderActions.issueSparePart),
      mergeMap(({ workOrderId, dto }) => this.svc.issueSparePart(workOrderId, dto).pipe(
        mergeMap(custody => [
          WorkOrderActions.issueSparePartSuccess({ workOrderId, custody }),
          WorkOrderActions.loadSparePartCustody({ workOrderId })
        ]),
        catchError(err => of(WorkOrderActions.workOrderFailure({ error: err.message })))
      ))
    )
  );

  clearSparePartCustody$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WorkOrderActions.clearSparePartCustody),
      mergeMap(({ workOrderId, dto }) => this.svc.clearSparePartCustody(workOrderId, dto).pipe(
        mergeMap(custody => [
          WorkOrderActions.clearSparePartCustodySuccess({ workOrderId, custody }),
          WorkOrderActions.loadSparePartCustody({ workOrderId })
        ]),
        catchError(err => of(WorkOrderActions.workOrderFailure({ error: err.message })))
      ))
    )
  );
}
